import { z } from 'zod';

import { IsoDateTimeSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * TBO provider geography (read-only reference, synced from the TBO hotel API).
 * Keyed by TBO's own codes/ids. See ADR-0019.
 */

// ---- Countries ----
export const TboCountryListItemSchema = z.object({
  code: z.string(),
  name: z.string(),
  syncedAt: IsoDateTimeSchema,
});
export type TboCountryListItem = z.infer<typeof TboCountryListItemSchema>;

export const TboCountryListQuerySchema = PaginationQuerySchema;
export type TboCountryListQuery = z.infer<typeof TboCountryListQuerySchema>;

export const TboCountryListResponseSchema = paginatedResponseSchema(TboCountryListItemSchema);
export type TboCountryListResponse = z.infer<typeof TboCountryListResponseSchema>;

// ---- States ----
export const TboStateListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  countryCode: z.string(),
  syncedAt: IsoDateTimeSchema,
});
export type TboStateListItem = z.infer<typeof TboStateListItemSchema>;

export const TboStateListQuerySchema = PaginationQuerySchema.extend({
  countryCode: z.string().trim().min(1).max(8).optional(),
});
export type TboStateListQuery = z.infer<typeof TboStateListQuerySchema>;

export const TboStateListResponseSchema = paginatedResponseSchema(TboStateListItemSchema);
export type TboStateListResponse = z.infer<typeof TboStateListResponseSchema>;

// ---- Cities ----
export const TboCityListItemSchema = z.object({
  cityId: z.string(),
  name: z.string(),
  countryCode: z.string(),
  stateId: z.string().nullable(),
  syncedAt: IsoDateTimeSchema,
});
export type TboCityListItem = z.infer<typeof TboCityListItemSchema>;

export const TboCityListQuerySchema = PaginationQuerySchema.extend({
  countryCode: z.string().trim().min(1).max(8).optional(),
  stateId: z.string().trim().min(1).optional(),
});
export type TboCityListQuery = z.infer<typeof TboCityListQuerySchema>;

export const TboCityListResponseSchema = paginatedResponseSchema(TboCityListItemSchema);
export type TboCityListResponse = z.infer<typeof TboCityListResponseSchema>;

// ---- City options (for the City Master dropdown) ----
export const TboCityOptionSchema = z.object({
  cityId: z.string(),
  name: z.string(),
});
export type TboCityOption = z.infer<typeof TboCityOptionSchema>;

export const TboCityOptionListSchema = z.array(TboCityOptionSchema);
export type TboCityOptionList = z.infer<typeof TboCityOptionListSchema>;

export const TboCityOptionsQuerySchema = z.object({
  countryCode: z.string().trim().min(1).max(8),
  q: z.string().trim().min(1).max(120).optional(),
});
export type TboCityOptionsQuery = z.infer<typeof TboCityOptionsQuerySchema>;

// ---- Sync ----
export const TboSyncResponseSchema = z.object({
  countries: z.number().int(),
  states: z.number().int(),
  cities: z.number().int(),
  syncedAt: IsoDateTimeSchema,
});
export type TboSyncResponse = z.infer<typeof TboSyncResponseSchema>;

/**
 * Current state of the synced TBO geography - row counts + when it was last
 * synced (`null` if never). Drives the "last synced" label and the
 * once-per-DB disable on the Sync button.
 */
export const TboSyncStatusSchema = z.object({
  countries: z.number().int(),
  states: z.number().int(),
  cities: z.number().int(),
  lastSyncedAt: IsoDateTimeSchema.nullable(),
});
export type TboSyncStatus = z.infer<typeof TboSyncStatusSchema>;
